import {
  LoaderFunctionArgs,
  MetaDescriptor,
  MetaFunction,
} from '@remix-run/node';
import { MetadataLoaderResponse, MetadataProps } from './meta-utils-types';

/**
 * Creates a `meta` function for Remix routes that extracts and formats metadata
 * from the loader's response. The loader is responsible for generating the page
 * translations and metadata, while the returned `meta` function renders the
 * metadata in site head.
 *
 * @example
 * export const loader = metadataLoader((t) => ({
 *   title: t('routes.dashboard.title'),
 *   description: t('routes.dashboard.title'),
 * }));
 *
 * export const meta = metadataMeta<typeof loader>();
 */
export function metadataMeta<
  T extends ({
    request,
  }: LoaderFunctionArgs) => Promise<MetadataLoaderResponse>,
>() {
  const meta: MetaFunction<T> = ({ data }) => {
    if (!data) return [];
    return generateMeta(data.meta);
  };
  return meta;
}

type GenerateMetaProps = {
  baseUrl: string;
} & MetadataProps;

const DEFAULT_IMAGE_URL = '/og_image.webp';

export function generateMeta({
  baseUrl,
  title,
  titleSuffix,
  description,
  type = 'website',
  image = DEFAULT_IMAGE_URL,
}: GenerateMetaProps): MetaDescriptor[] {
  return [
    // Basic site metadata
    { name: 'og:type', content: type },

    ...generateTitleMeta(title, titleSuffix),
    ...generateDescriptionMeta(description),
    ...generateImageMeta(baseUrl, image),
  ];
}

function generateTitleMeta(
  title: string,
  titleSuffix?: string,
): MetaDescriptor[] {
  const fullTitle = titleSuffix ? `${title} | ${titleSuffix}` : title;
  return [{ title: fullTitle }, { name: 'og:title', content: fullTitle }];
}

function generateDescriptionMeta(description?: string): MetaDescriptor[] {
  if (!description) return [];
  return [
    { name: 'description', content: description },
    { name: 'og:description', content: description },
  ];
}

function generateImageMeta(baseUrl?: string, image?: string): MetaDescriptor[] {
  if (!image || !baseUrl) return [];
  const fullUrl = new URL(image, baseUrl).toString();
  return [{ name: 'og:image', content: fullUrl }];
}
